import React, { useState } from 'react';
import { Calendar, Clock, User, RotateCcw, AlertCircle, CheckCircle } from 'lucide-react';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { useTheme } from '../contexts/ThemeContext';

const ClientAddAppointmentModal = ({ date, employees, onSave, onClose, initialEmployeeId }) => {
  const { theme } = useTheme();
  const isDarkMode = document.documentElement.classList.contains('dark');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [appointmentData, setAppointmentData] = useState(() => {
    const initialDate = new Date(date);
    initialDate.setMinutes(initialDate.getMinutes() - initialDate.getTimezoneOffset());
    return {
      date: initialDate,
      startTime: '',
      endTime: '',
      employeeId: initialEmployeeId || '',
      status: initialEmployeeId ? 'assigne' : 'libre',
      isRecurring: false,
      recurrenceBase: 'weekly',
      recurrenceFrequency: 1, // UNIFIÉ: utilisé pour daily, weekly ET monthly
      recurrenceDay: [initialDate.getDay()],
      recurrenceMonthDay: initialDate.getDate(),
      recurrenceMonthType: 'dayOfMonth',
      recurrenceMonthWeek: Math.floor((initialDate.getDate() - 1) / 7) + 1,
      recurrenceMonthWeekday: initialDate.getDay(),
      recurrenceEndType: 'never',
      recurrenceEndAfter: 1,
      recurrenceEndDate: null,
    };
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAppointmentData(prev => {
      let newValue = value;

      if (type === 'checkbox') {
        newValue = checked;
      } else if (name === 'recurrenceFrequency') {
        newValue = Math.max(1, parseInt(value, 10) || 1);
      } else if (name === 'date') {
        newValue = value ? new Date(value) : new Date();
      }

      const newData = {
        ...prev,
        [name]: newValue
      };

      if (name === 'employeeId') {
        newData.status = value ? 'assigne' : 'libre';
      }

      return newData;
    });
  };

  const handleDaySelection = (day) => {
    setAppointmentData(prev => ({
      ...prev,
      recurrenceDay: prev.recurrenceDay.includes(day)
        ? prev.recurrenceDay.filter(d => d !== day)
        : [...prev.recurrenceDay, day]
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!appointmentData.startTime) {
      newErrors.startTime = 'L\'heure de début est requise';
    }

    if (!appointmentData.endTime) {
      newErrors.endTime = 'L\'heure de fin est requise';
    }

    if (appointmentData.startTime && appointmentData.endTime) {
      const start = new Date(`2000-01-01T${appointmentData.startTime}`);
      const end = new Date(`2000-01-01T${appointmentData.endTime}`);
      if (end <= start) {
        newErrors.endTime = 'L\'heure de fin doit être après l\'heure de début';
      }
    }

    // NOUVELLE VALIDATION: vérifier qu'au moins un jour est sélectionné pour weekly
    if (appointmentData.isRecurring &&
      appointmentData.recurrenceBase === 'weekly' &&
      appointmentData.recurrenceDay.length === 0) {
      newErrors.recurrenceDay = 'Veuillez sélectionner au moins un jour de la semaine';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const dateWithTime = new Date(appointmentData.date);
      dateWithTime.setUTCHours(2, 0, 0, 0);
      await onSave({
        ...appointmentData,
        date: dateWithTime
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du rendez-vous:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getDayOfWeekName = (day) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    return days[day];
  };

  const getWeekOfMonthName = (week) => {
    const weeks = ['premier', 'deuxième', 'troisième', 'quatrième', 'dernier'];
    return weeks[Math.min(week - 1, 4)];
  };

  // Helper safe date string
  const getDateString = (dateVal) => {
    if (!dateVal) return '';
    if (dateVal instanceof Date && !isNaN(dateVal)) {
      return dateVal.toISOString().split('T')[0];
    }
    // Si c'est déjà une string (cas limite), on la retourne telle quelle si elle ressemble à une date
    if (typeof dateVal === 'string') return dateVal;
    return '';
  };

  return (
    <EnhancedModal isOpen={true} onClose={onClose} size="xl">
      <ModalHeader onClose={onClose}>
        Ajouter un rendez-vous
      </ModalHeader>

      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Section Informations de base */}
          <div className="space-y-4">
            <h3 className={`text-lg font-semibold border-b pb-2 ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-200'}`}>
              Informations de base
            </h3>

            {/* Date */}
            <div className="space-y-2">
              <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <Calendar className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                Date du rendez-vous
              </label>
              <input
                type="date"
                name="date"
                value={getDateString(appointmentData.date)}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              />
            </div>

            {/* Horaires */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Clock className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                  Heure de début
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={appointmentData.startTime}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.startTime
                    ? 'border-red-300 bg-red-50'
                    : isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                {errors.startTime && (
                  <div className="flex items-center text-sm text-red-600">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.startTime}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Clock className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                  Heure de fin
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={appointmentData.endTime}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.endTime
                    ? 'border-red-300 bg-red-50'
                    : isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                    }`}
                />
                {errors.endTime && (
                  <div className="flex items-center text-sm text-red-600">
                    <AlertCircle size={14} className="mr-1" />
                    {errors.endTime}
                  </div>
                )}
              </div>
            </div>

            {/* Intervenant */}
            <div className="space-y-2">
              <label className={`flex items-center text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <User className={`mr-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-500'}`} size={16} />
                Intervenant assigné
              </label>
              <select
                name="employeeId"
                value={appointmentData.employeeId}
                onChange={handleChange}
                className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                <option value="">Sélectionner un intervenant...</option>
                {employees.map(emp => (
                  <option key={emp.userId} value={emp.userId}>
                    {emp.name || `${emp.firstName} ${emp.lastName}`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Section Récurrence */}
          <div className="space-y-4">
            <div className={`flex items-center space-x-3 p-4 rounded-lg border ${isDarkMode
              ? 'bg-gray-800 border-gray-700'
              : 'bg-gray-50 border-gray-200'
              }`}>
              <div className={`p-2 rounded-full ${appointmentData.isRecurring
                ? isDarkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-600'
                : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-600'
                }`}>
                <RotateCcw size={20} />
              </div>
              <div className="flex-1">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isRecurring"
                    checked={appointmentData.isRecurring}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`w-10 h-6 rounded-full transition-colors duration-200 ease-in-out ${appointmentData.isRecurring ? 'bg-green-500' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                    }`}>
                    <div className={`bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out transform ${appointmentData.isRecurring ? 'translate-x-6' : 'translate-x-1'
                      } mt-1`} />
                  </div>
                  <span className={`ml-3 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    Rendez-vous récurrent
                  </span>
                </label>
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Planifier ce rendez-vous de manière répétée selon une fréquence définie
                </p>
              </div>
            </div>
          </div>

          {appointmentData.isRecurring && (
            <div className={`space-y-4 p-4 rounded-lg border ${isDarkMode
              ? 'bg-blue-900/20 border-blue-700'
              : 'bg-blue-50 border-blue-200'
              }`}>
              <h4 className={`font-medium flex items-center ${isDarkMode ? 'text-blue-300' : 'text-blue-900'}`}>
                <RotateCcw className="mr-2" size={16} />
                Paramètres de récurrence
              </h4>
              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Fréquence</label>
                <select
                  name="recurrenceBase"
                  value={appointmentData.recurrenceBase}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-blue-300 text-gray-900'
                    }`}
                >
                  <option value="daily">Journalière</option>
                  <option value="weekly">Hebdomadaire</option>
                  <option value="monthly">Mensuelle</option>
                </select>
              </div>

              {appointmentData.recurrenceBase === 'daily' && (
                <div className={`flex items-center space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tous les</span>
                  <input
                    type="number"
                    name="recurrenceFrequency"
                    value={appointmentData.recurrenceFrequency || 1}
                    onChange={handleChange}
                    min="1"
                    className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 ${isDarkMode
                      ? 'bg-gray-600 border-gray-500 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>jour(s)</span>
                </div>
              )}

              {appointmentData.recurrenceBase === 'weekly' && (
                <>
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tous les</span>
                    <input
                      type="number"
                      name="recurrenceFrequency"
                      value={appointmentData.recurrenceFrequency}
                      onChange={handleChange}
                      min="1"
                      className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 ${isDarkMode
                        ? 'bg-gray-600 border-gray-500 text-gray-100'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>semaine(s)</span>
                  </div>
                  <div className="space-y-2">
                    <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Jours de la semaine</label>
                    <div className="flex flex-wrap gap-2">
                      {[0, 1, 2, 3, 4, 5, 6].map(day => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => handleDaySelection(day)}
                          className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${appointmentData.recurrenceDay.includes(day)
                            ? isDarkMode ? 'bg-blue-600 text-white shadow-md' : 'bg-blue-500 text-white shadow-md'
                            : isDarkMode ? 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {getDayOfWeekName(day).slice(0, 3)}
                        </button>
                      ))}
                    </div>
                    {errors.recurrenceDay && (
                      <div className="flex items-center text-sm text-red-600 dark:text-red-400 mt-2">
                        <AlertCircle size={14} className="mr-1" />
                        {errors.recurrenceDay}
                      </div>
                    )}
                  </div>
                </>
              )}

              {appointmentData.recurrenceBase === 'monthly' && (
                <>
                  <div>
                    <select
                      name="recurrenceMonthType"
                      value={appointmentData.recurrenceMonthType}
                      onChange={handleChange}
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-100'
                        : 'bg-white border-blue-300 text-gray-900'
                        }`}
                    >
                      <option value="dayOfMonth">Le même jour du mois</option>
                      <option value="dayOfWeek">Le même jour de la semaine</option>
                    </select>
                  </div>
                  {appointmentData.recurrenceMonthType === 'dayOfMonth' ? (
                    <div className={`text-sm p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                      }`}>
                      Répéter le {appointmentData.recurrenceMonthDay} de chaque mois
                    </div>
                  ) : (
                    <div className={`text-sm p-2 rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-300' : 'bg-white border-gray-300 text-gray-700'
                      }`}>
                      Répéter le {getWeekOfMonthName(appointmentData.recurrenceMonthWeek)} {getDayOfWeekName(appointmentData.recurrenceMonthWeekday)} de chaque mois
                    </div>
                  )}
                  <div className={`flex items-center space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}>
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Tous les</span>
                    <input
                      type="number"
                      name="recurrenceFrequency"
                      value={appointmentData.recurrenceFrequency}
                      onChange={handleChange}
                      min="1"
                      className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 ${isDarkMode
                        ? 'bg-gray-600 border-gray-500 text-gray-100'
                        : 'bg-white border-gray-300 text-gray-900'
                        }`}
                    />
                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>mois</span>
                  </div>
                </>
              )}

              <div className="space-y-2">
                <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Fin de la récurrence</label>
                <select
                  name="recurrenceEndType"
                  value={appointmentData.recurrenceEndType}
                  onChange={handleChange}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-blue-300 text-gray-900'
                    }`}
                >
                  <option value="never">Jamais</option>
                  <option value="after">Après un nombre d'occurrences</option>
                  <option value="on">À une date spécifique</option>
                </select>
              </div>
              {appointmentData.recurrenceEndType === 'after' && (
                <div className={`flex items-center space-x-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                  }`}>
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Après</span>
                  <input
                    type="number"
                    name="recurrenceEndAfter"
                    value={appointmentData.recurrenceEndAfter}
                    onChange={handleChange}
                    min="1"
                    className={`w-20 p-2 border rounded text-center focus:ring-2 focus:ring-blue-500 ${isDarkMode
                      ? 'bg-gray-600 border-gray-500 text-gray-100'
                      : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                  <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>occurrences</span>
                </div>
              )}
              {appointmentData.recurrenceEndType === 'on' && (
                <div className="space-y-2">
                  <label className={`text-sm font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-800'}`}>Date de fin</label>
                  <input
                    type="date"
                    name="recurrenceEndDate"
                    value={appointmentData.recurrenceEndDate || ''}
                    onChange={handleChange}
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${isDarkMode
                      ? 'bg-gray-700 border-gray-600 text-gray-100'
                      : 'bg-white border-blue-300 text-gray-900'
                      }`}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-end space-x-2 w-full">
          <ModalButton
            variant="secondary"
            onClick={onClose}
          >
            Annuler
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Ajout en cours...' : 'Ajouter'}
          </ModalButton>
        </div>
      </ModalFooter>
    </EnhancedModal>
  );
};

export default ClientAddAppointmentModal;
